import React from "react";
import { Link } from "react-router-dom";

const FAQSection = () => {
  const faqs = [
    {
      q: "Do I need a backend to use ContactHQ?",
      a: "No! ContactHQ is a no-backend contact form solution. Simply create your form, copy the embed code, and it's live. All submissions are handled securely on our platform.",
      icon: "cloud_off",
    },
    {
      q: "Is ContactHQ secure?",
      a: "Absolutely. Every submission is sent to a secure form endpoint, encrypted, and stored safely according to industry-standard practices.",
      icon: "gpp_good",
    },
    {
      q: "Can I manage multiple forms and clients?",
      a: "Yes! With our paid plans, you can manage multiple active forms. Our centralized inbox lets you view, assign, and tag submissions from different clients in one place.",
      icon: "layers",
    },
    {
      q: "Can my team collaborate on responses?",
      a: "Definitely. Assign messages, leave internal notes, and track which leads are open or resolved. It’s a shared inbox built specifically for forms.",
      icon: "group_add",
    },
    {
      q: "How do I embed the form on my website?",
      a: "Once ready, we provide a copy-paste snippet or CDN link. Works on WordPress, Shopify, Netlify, Vercel, or any plain HTML page instantly.",
      icon: "code",
    },
    {
      q: "Can I customize the form design?",
      a: "Yes! Adjust colors, fonts, and field types. Paid plans also allow you to remove ContactHQ branding for a fully professional look.",
      icon: "palette",
    },
    {
      q: "Does ContactHQ work in India and globally?",
      a: "Yes. Optimized for small businesses and developers in India, but fully reliable and fast for users no matter where they are located.",
      icon: "public",
    },
    {
      q: "What if I want to upgrade my plan?",
      a: "Upgrading is simple. Move from Free to Diamond anytime. Your existing forms and submissions remain intact during the transition.",
      icon: "rocket_launch",
    },
    {
      q: "Can I export my form submissions?",
      a: "Yes! All plans allow you to export responses to CSV or Excel for data analysis or reporting.",
      icon: "download",
    },
    {
      q: "What support do you offer?",
      a: "All plans include support, with priority response for Silver, Gold, and Diamond users to ensure your forms run smoothly.",
      icon: "support_agent",
    },
  ];

  return (
    <section id="faq" className="py-16 sm:py-20 lg:py-24 bg-slate-50 border-t border-[#E2E8F0]">
      <div className="mx-auto w-[95%] md:w-[90%]">
        {/* HEADER */}
        <div className="max-w-3xl mb-10 sm:mb-16 animate-fade-in-up">
          <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
            Common Inquiries
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl mb-4 sm:mb-6 leading-tight">
            Got questions? <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] to-[#E63946]">
              We’ve got answers.
            </span>
          </h3>
          <p className="text-base sm:text-lg text-[#64748B]">
            ContactHQ is designed to be simple and powerful. Here’s everything
            you need to know about how it works, pricing, and setup.
          </p>
        </div>

        {/* FAQ GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 sm:gap-y-10">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="flex gap-4 sm:gap-6 opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex-none flex items-center justify-center size-10 rounded-xl bg-white text-[#E63946] border border-[#E2E8F0] shadow-sm">
                <span className="material-symbols-outlined text-xl">
                  {faq.icon}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-[#0B1C2D] mb-3">
                  {faq.q}
                </h4>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* FINAL HELP CTA */}
        <div className="mt-12 sm:mt-20 p-6 sm:p-10 rounded-3xl sm:rounded-[2.5rem] bg-[#0B1C2D] text-center text-white relative overflow-hidden opacity-0 animate-fade-in-up [animation-delay:800ms]">
          <div className="relative z-10">
            <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Still have a question?</h4>
            <p className="text-slate-400 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base">
              Our support team is always here to help you build better customer
              connections. Drop us a message and we'll get back to you within 24
              hours.
            </p>
            <Link to={"/contact"} className="inline-flex items-center gap-2 rounded-xl bg-[#E63946] px-6 sm:px-8 py-3.5 sm:py-4 font-bold hover:bg-[#d12f3c] transition-all duration-300">
              Contact Support
              <span className="material-symbols-outlined">mail</span>
            </Link>
          </div>
          {/* Decorative Glow */}
          <div className="absolute top-0 right-0 size-64 bg-[#E63946]/10 blur-[100px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 size-64 bg-[#E63946]/5 blur-[100px] rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
